import { useCurrentPageAccess } from "@/app/router";
import { ConfirmDialog } from "@/shared/components/feedback/ConfirmDialog";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { useDialog } from "@/shared/hooks/useDialog";
import { Plus, RotateCw } from "lucide-react";
import { DataForwarderPolicyCardList } from "../components/DataForwarderPolicyCardList";
import { DataForwarderPolicyDetailDialog } from "../components/DataForwarderPolicyDetailDialog";
import { DataForwarderPolicyWizardDialog } from "../components/DataForwarderPolicyWizardDialog";
import { useDataForwarderPolicies } from "../hooks/useDataForwarderPolicies";
import type { DataForwarderPolicy } from "../types/preferences_types";

export function PreferencesDataForwarderPolicyPage() {
  const df = useDataForwarderPolicies();
  const { canWriteCurrentPage } = useCurrentPageAccess();

  const detailDialog = useDialog<DataForwarderPolicy>();
  const deleteDialog = useDialog<DataForwarderPolicy>();

  const handleCardClick = (policy: DataForwarderPolicy) => {
    detailDialog.open(policy);
  };

  const handleEditFromDetail = (id: number) => {
    detailDialog.close();
    df.openEdit(id);
  };

  const handleDeleteFromDetail = (id: number) => {
    const policy = df.policies.find((p) => p.id === id);
    if (policy) {
      detailDialog.close();
      deleteDialog.open(policy);
    }
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="데이터 포워딩 정책"
        description="데이터 포워딩 정책을 관리합니다."
        actions={
          <>
            <Button variant="outline" onClick={df.reload}>
              <RotateCw className="mr-2 h-4 w-4" />
              새로고침
            </Button>
            <Button onClick={df.openCreate} disabled={!canWriteCurrentPage}>
              <Plus className="mr-2 h-4 w-4" />
              추가
            </Button>
          </>
        }
      />

      <DataForwarderPolicyCardList
        policies={df.policies}
        isLoading={df.isLoading}
        onCardClick={handleCardClick}
      />

      <DataForwarderPolicyDetailDialog
        open={detailDialog.isOpen}
        onOpenChange={(open) => !open && detailDialog.close()}
        policy={detailDialog.data ?? null}
        canWrite={canWriteCurrentPage}
        onEdit={handleEditFromDetail}
        onDelete={handleDeleteFromDetail}
      />

      <DataForwarderPolicyWizardDialog
        open={df.open}
        onOpenChange={(open) => {
          if (!open) {
            df.setOpen(false);
          }
        }}
        title={`Data Forwarder 정책 ${df.mode === "create" ? "추가" : "수정"}`}
        step={df.step}
        onPrev={df.prev}
        onNext={df.next}
        onCancel={() => df.setOpen(false)}
        onFinalize={df.finalize}
        errorMsg={df.errorMsg}
        organizations={df.organizations}
        channels={df.channels}
        forwardingType={df.forwardingType}
        setForwardingType={df.setForwardingType}
        policyName={df.policyName} setPolicyName={df.setPolicyName}
        orgId={df.orgId} setOrgId={df.setOrgId}
        workspaceId={df.workspaceId} setWorkspaceId={df.setWorkspaceId}
        channel={df.channel} setChannel={df.setChannel}
        subChannel={df.subChannel} setSubChannel={df.setSubChannel}
        dwAccountId={df.dwAccountId} setDwAccountId={df.setDwAccountId}
        reports={df.reports} setReports={df.setReports}
        externalId={df.externalId}
        regions={df.regions}
        bucketRegion={df.bucketRegion} setBucketRegion={df.setBucketRegion}
        bucketName={df.bucketName} setBucketName={df.setBucketName}
        roleArn={df.roleArn} setRoleArn={df.setRoleArn}
        prefix={df.prefix} setPrefix={df.setPrefix}
        targetServiceAccount={df.targetServiceAccount} setTargetServiceAccount={df.setTargetServiceAccount}
        connectionTestStatus={df.connectionTestStatus}
        connectionTestMessage={df.connectionTestMessage}
        onRunConnectionTest={df.runConnectionTest}
      />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="정책 삭제"
        description={`"${deleteDialog.data?.policy_name}" 정책을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`}
        confirmText="삭제"
        variant="destructive"
        onConfirm={async () => {
          if (deleteDialog.data) {
            await df.deletePolicies([deleteDialog.data.id]);
            deleteDialog.close();
            await df.reload();
          }
        }}
      />
    </div>
  );
}
